import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { debounceTime, filter, mergeMap, tap } from 'rxjs/operators';
import { IEntity, IRoute, TokenService } from 'viggo-sistemas-core-web';
import { UserDisponivelService } from './users-disponivel.service';

// const components = {
//   condicao_pgmt: {
//     component: FastCondPgmtComponent,
//     config: {
//       width: '800px',
//       data: {
//         entity: {
//           parcelas: []
//         } as ICondicaoPagamento
//       }
//     }
//   }
// }

@Component({
  selector: 'autocomplete-users',
  templateUrl: './autocomplete-users.component.html',
  providers: [UserDisponivelService]
})
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class AutoCompleteUsers<T extends IEntity = any> implements OnInit {
  @Input() user_id: string;
  @Input() routesAuthorization: IRoute[] = [];
  @Input() data: any = {};
  /**
   * Placeholder usado pelo input
   */
  @Input() placeholder = 'Digite aqui';
  /**
   * Exibe mensagem de erro, caso haja
   */
  @Input() errorMsg = '';
  /**
   * Mensagens de erro
   */
  @Input() messages: { [key: string]: string };

  @Input() displayField = 'id';

  @Input() displaySecondField = 'id';
  /**
   * Tipo de input do autocomplete
   */
  @Input() inputType = 'text';
  /**
   * Identifica se o campo é de preenchimento obrigatório
   */
  @Input() public readonly required = false;
  /**
   * Função para exibir os dados do autocomplete
   * @deprecated
   */
  @Input() displayWith: (obj: T) => string = obj => obj.toString();
  /**
   * O FormControl responsável por gerenciar o input
   * @required
   */
  @Input() control: UntypedFormControl;
  /**
   * Configuração de selecionar automaticamente o uncio resultado
   */
  @Input() public readonly autoFirst: boolean = true;

  /**
   * Configuração de exibir automaticamente os primeiros resultados
   */
  @Input() public notShowResultsFirst: boolean = true;
  /**
   * Inicia dados preenchido quando a busca for vazia
   */

  @Input() public readonly initDataWithEmpty: boolean = true;

  /**
   * Caso o usuario queira repassar sua própria função de busca
   * É possível atravês dessa props
   */
  @Input() public readonly searchFN: (
    value: string
  ) => Observable<any[]> = null;
  /**
   * Função chamada sempre que há uma requisição a API
   * Implementa lógica caso seja nescessário filtar as entidades
   * vindas do banco de dados, ex: impedir auto relacionamento de entidades
   * @param entities entidades vindas da requisição
   * @tutorial rulesFilterEntities Cuidado com a perda de contexto do "this" use arrow functions
   * ou tenha certeza que o contexto chamado seja da sua classe externa com o bind.
   */
  @Input() rulesFilterEntities: (entities: any) => any = entities => entities;

  /**
   * Quando os resultados são carregados da api, este evento é acionado, repassando
   * os resultados retornados da api
   */
  @Output() public onLoadResults = new EventEmitter<any>();

  filteredEntities: any = [];
  /**
   * Id do dominio onde a infomação deve ser pesquisada
   */
  @Input() cliente_id: string;

  @Input() controlIsEnabled = true;

  @Input() domain_id: string;

  @Input() value_order: string;

  @Input() pageValue: number;

  @Input() pageSizeValue: number;

  @Input() public readonly include: string[] = null;

  /**
   * Mostra a entidade selecionada
   * @param entity entidade a ser mostrada
   */
  @Input() displayWithEntityFn = (entity: T | string) => {
    if (!entity) return '';
    if (typeof entity === 'string') return entity;
    return entity[this.displayField];
  };
  @Output() onSelect = new EventEmitter<T>();
  @Output() public clearSelection = new EventEmitter<boolean>();

  /**
   * É possivel repassar paremetros adicionais para a requisição
   */
  @Input() params: { [param: string]: string } = {};

  @Input() readonly = false;

  @Input() actionState = true;

  /**
   * Emite eventos sempre que o autocomplete
   * precisar forçar a seleção de uma entidade
   * @description Emite o evento quando a entidade é selecionada
   */
  @Output() autoSelect: EventEmitter<T> = new EventEmitter();
  @Output() selectEntity: EventEmitter<T> = new EventEmitter();
  @Output() blur: EventEmitter<FocusEvent> = new EventEmitter();

  public notShowOptions = false;
  public isLoading = false;
  public noResults = false;
  public isEmtpty = false;

  constructor(
    protected userService: UserDisponivelService,
    protected tokenService: TokenService,
    public matDialog: MatDialog
  ) {}

  ngOnInit() {
    if (this.notShowResultsFirst) {
      this.isLoading = false;
      this.filteredEntities = [];
    } else {
      this.userService
        .usuariosDisponiveis(
          this.domain_id,
          this.user_id,
          this.include,
          {
            require_pagination: true,
            page_size: this.pageSizeValue,
            page: this.pageValue
          },
          this.value_order
        )
        .subscribe(entities => {
          this.isLoading = false;
          this.filteredEntities = entities.user;
        });
    }
    this.control.valueChanges
      .pipe(
        debounceTime(300),
        filter(() => {
          // caso o valor selecionado seja um objeto, ele não faz uma requisição ao banco
          if (typeof this.control.value === 'object')
            this.selectEntity.emit(this.control.value);
          return typeof this.control.value === 'string';
        }),
        tap(() => {
          this.isLoading = true;
          this.noResults = false;
          this.notShowOptions = false;
        }),
        mergeMap(text => {
          if (text && text !== '') {
            if (!text.replace(/\s/g, '').length) {
              this.isEmtpty = true;
              return of([]);
            }
            this.isEmtpty = false;
            const textFilter = text.trim();
            return this.userService.usuariosDisponiveis(
              this.domain_id,
              this.user_id,
              this.include,
              {
                require_pagination: true,
                page_size: this.pageSizeValue,
                page: this.pageValue
              },
              this.value_order,
              { field: 'name', value: `${textFilter}%` }
            );
          } else {
            if (this.control.valid) {
              this.isEmtpty = false;
              return this.userService.usuariosDisponiveis(
                this.domain_id,
                this.user_id,
                this.include,
                {
                  require_pagination: true,
                  page_size: this.pageSizeValue,
                  page: this.pageValue
                },
                this.value_order
              );
            } else {
              this.isEmtpty = true;
              return of([]);
            }
          }
        })
      )
      .subscribe((entities: any) => {
        this.isLoading = false;
        this.notShowResultsFirst = false;
        this.filteredEntities = this.isEmtpty ? [] : entities.user;
        if (
          this.filteredEntities &&
          this.filteredEntities.length === 1 &&
          this.autoFirst
        ) {
          this.control.setValue(this.filteredEntities[0]);
          this.autoSelect.emit(this.filteredEntities[0]);
          this.onSelect.emit(this.filteredEntities[0]);
        }
      });
  }

  getParamsByInclude(includes: string[]): string {
    if (includes.length > 0) return `&include=${includes.join(',')}`;
    return '';
  }
  getKey(): string {
    return Object.keys(this.control.errors)[0];
  }

  clearSelectValue(){
    this.notShowOptions = false;
    this.clearSelection.emit(true);
  }


  // openDialogFast(): void {
  //   this.matDialog
  //     .open(components.condicao_pgmt.component, {
  //       ...components.condicao_pgmt.config,
  //       disableClose: true,
  //       panelClass: ['dialog-sem-padding'],
  //       position: {
  //         top: '50px'
  //       },
  //       data: {
  //         ...components.condicao_pgmt.config.data,
  //         ...this.data
  //       }
  //     })
  //     .afterClosed()
  //     .subscribe(componentEntity => {
  //       this.notShowOptions = false;
  //       if (componentEntity) {
  //         this.control.setValue(componentEntity);
  //         this.filteredEntities = [componentEntity, ...this.filteredEntities];
  //       } else {
  //         this.control.setValue(null);
  //       }
  //     });
  // }
}
